/**
 * Connexion par code (OTP) : le code envoyé survit à un aller-retour dans la boîte mail.
 *
 * Constat (retours d'élèves, 29/10/2026) : beaucoup trouvaient leur code « expiré » en revenant de leur boîte
 * mail. Le code ne l'était pas : en quittant l'app (iOS, Android) ou l'onglet, la page se recharge, l'écran
 * repart sur « Ton email », l'élève redemande un code… et Supabase ANNULE le précédent — celui qu'il vient de
 * lire. D'où :
 *   - l'email et l'heure d'envoi sont gardés sur l'appareil (jamais le code) : au retour, l'écran du code
 *     revient tel quel ;
 *   - redemander un code pour la même adresse peu après n'en renvoie pas un nouveau : celui du dernier email
 *     reste bon (« Renvoyer le code » reste possible, explicitement) ;
 *   - les messages de Supabase sont traduits et disent quoi faire.
 * PUR (stockage injecté pour les tests).
 */

const KEY = 'jr_otp_pending';

/** Durée de validité d'un code (Supabase → Authentication → Email OTP Expiration : 3 600 s). */
export const OTP_VALID_MS = 60 * 60_000;
/** Un code envoyé il y a moins de 15 min à la même adresse est réutilisé (pas de nouvel envoi automatique). */
export const OTP_REUSE_MS = 15 * 60_000;

export interface PendingOtp {
  email: string;
  sentAt: number;
}

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

function store(): Store | null {
  try {
    return typeof window === 'undefined' ? null : window.localStorage;
  } catch {
    return null;
  }
}

/** Le code en attente (envoyé il y a moins d'une heure, moins une minute de marge), sinon null. */
export function readPendingOtp(now = Date.now(), s: Store | null = store()): PendingOtp | null {
  try {
    const raw = JSON.parse(s?.getItem(KEY) ?? 'null') as Partial<PendingOtp> | null;
    if (!raw || typeof raw.email !== 'string' || typeof raw.sentAt !== 'number') return null;
    if (now - raw.sentAt < 0 || now - raw.sentAt > OTP_VALID_MS - 60_000) return null;
    return { email: raw.email, sentAt: raw.sentAt };
  } catch {
    return null;
  }
}

export function savePendingOtp(email: string, now = Date.now(), s: Store | null = store()): void {
  try {
    s?.setItem(KEY, JSON.stringify({ email, sentAt: now }));
  } catch {
    /* stockage indisponible : l'écran du code ne survivra pas à un rechargement */
  }
}

export function clearPendingOtp(s: Store | null = store()): void {
  try {
    s?.removeItem(KEY);
  } catch {
    /* rien à effacer */
  }
}

/** Un code récent existe pour cette adresse : ne pas en renvoyer un (il annulerait celui de l'email). */
export function reusablePendingOtp(
  email: string,
  now = Date.now(),
  s: Store | null = store(),
): PendingOtp | null {
  const p = readPendingOtp(now, s);
  return p && p.email === email && now - p.sentAt < OTP_REUSE_MS ? p : null;
}

/** « 14:05 » */
export function otpTime(at: number): string {
  const d = new Date(at);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/** Message d'erreur de Supabase Auth → français, avec quoi faire. */
export function otpErrorMessage(err: unknown): string {
  const e = (err ?? {}) as { message?: string; code?: string };
  const msg = e.message ?? '';
  const wait = /after (\d+) seconds?/i.exec(msg)?.[1];
  if (wait) return `Patiente ${wait} s avant de redemander un code.`;
  if (e.code === 'otp_expired' || /expired|invalid/i.test(msg))
    return 'Ce code ne marche pas. Prends celui du DERNIER email reçu : chaque nouveau code annule le précédent. Sinon, touche « Renvoyer le code ».';
  if (e.code === 'over_email_send_rate_limit' || /rate limit/i.test(msg))
    return 'Trop de codes demandés d’un coup : patiente quelques minutes, ou utilise le code du dernier email reçu.';
  if (/captcha/i.test(msg))
    return 'La vérification anti-robot a échoué : recharge la page et réessaie.';
  return msg || 'Une erreur est survenue, réessaie.';
}
